import type { FacetValue } from '../types'

/**
 * Converts a record of Algolia facet values into a sorted array of FacetValue objects for UI filters.
 *
 * If splitKeys is true, assumes each key starts with a position (e.g. "1 Black") and splits it into
 * a position (used for sorting) and a label (shown in the UI). If splitKeys is false, treats the
 * whole key as the label and sets position to 0.
 *
 * Filters out values with a count of 0 and sorts the results by position.
 *
 * @param algoliaFacetValues - An object where each key is a facet value (optionally prefixed with a position)
 *   and each value is the count for that facet.
 * @param splitKeys - Set to true to extract position from the start of each key (default: false).
 * @returns An array of FacetValue objects ready for rendering in UI filters.
 *
 * @example
 * // Typical usage with splitKeys:
 * transformFacetsValues({ "1 Black": 10, "2 White": 5, "3 Blue": 0 }, true);
 * // => [
 * //   { position: 1, label: "Black", name: "1 Black", count: 10 },
 * //   { position: 2, label: "White", name: "2 White", count: 5 }
 * // ]
 */
export const transformFacetsValues = (
  algoliaFacetValues?: Record<string, number>,
  splitKeys = false,
): FacetValue[] => {
  if (!algoliaFacetValues) return []

  const result: FacetValue[] = []

  for (const [key, count] of Object.entries(algoliaFacetValues)) {
    if (count <= 0) continue

    let position = 0
    let label = key

    if (splitKeys) {
      const parts = key.split(' ')
      position = parseInt(parts[0], 10)
      label = parts.slice(1).join(' ')
    }

    result.push({
      position,
      label,
      name: key,
      count,
    })
  }

  // Sort by position if needed (splitKeys), otherwise keeps original order
  if (splitKeys) {
    result.sort((a, b) => a.position - b.position)
  }

  return result
}
